/**
 * A workspace's enabled parts, as small violet tags — "REACH · CONNECT · SENSE".
 *
 * WHY A TAG AND NOT A BADGE. Until slice 66 these were `Pill`s in the neutral
 * tone, drawn with the same rounded, bordered capsule as the status beside them
 * ("ACTIVE", "EXPIRED"). In a table row that put four capsules of one shape next
 * to each other, and the one that mattered — whether anybody can sign in — was
 * found by colour alone. A feature is not a status: it is which parts of Wend
 * this plan includes. So it is drawn as `@wend/ui`'s `Tag` — square-cornered,
 * borderless, smaller — and the status keeps the capsule. Brand tone because
 * the brand's violet means "a part of Wend" everywhere in the product (the
 * Badge header says the same about its `brand` tone), and never good or bad.
 *
 * The rejected alternative was the neutral Tag: grey on grey reads as disabled,
 * and an enabled feature shown as disabled is the opposite of the fact.
 *
 * ORDER is `inPillarOrder` — Reach, Connect, Sense, Flow — not the order the API
 * returns, for the reason `lib/features.ts` gives. An empty set says "None" in
 * words rather than rendering nothing, so an empty cell cannot be mistaken for
 * a row that failed to load.
 *
 * THE HOLE: a tag carries the short name only. What a part does is on the
 * edit form (`FeaturePicker`), not here; this is a directory, not a contract.
 */
import type { Feature } from '@wend/shared';
import { Tag, cn } from '@wend/ui';

import { inPillarOrder, shortFeatureLabel } from '../lib/features';

export interface FeatureTagsProps {
  features: readonly Feature[];
  className?: string;
}

export function FeatureTags({ features, className }: FeatureTagsProps) {
  if (features.length === 0) {
    return <span className={cn('text-[0.8rem] text-muted', className)}>None</span>;
  }

  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {inPillarOrder(features).map((feature) => (
        <Tag key={feature} tone="brand">
          {shortFeatureLabel(feature)}
        </Tag>
      ))}
    </span>
  );
}
